import React, { useEffect, useRef, useState } from 'react';
import mapboxgl from 'mapbox-gl';
import 'mapbox-gl/dist/mapbox-gl.css';
import { useDispatch, useSelector } from 'react-redux';
import { setAddressState } from '@/store/slice';
import { useNavigate } from 'react-router-dom';
import { RootState } from '@store';
import { geoJsonValueSelector } from '@utils';
import { CustomSpinner } from '../customSpinner';
import { IoMdClose } from 'react-icons/io';
import { Location } from '@interface';

mapboxgl.accessToken = process.env.REACT_APP_MAPBOX_TOKEN;

export const DistrictMap: React.FC<{
	locations: Location[];
	mapHeight?: string;
	currentModule?: string;
}> = ({ locations, mapHeight = 'h-screen', currentModule }) => {
	const mapContainerRef = useRef<HTMLDivElement | null>(null);
	const mapRef = useRef<mapboxgl.Map | null>(null);
	const [districtJson, setDistrictJson] = useState<GeoJSON.FeatureCollection | null>(null);
	const [borderKey, setBorderKey] = useState<string>('');
	const markersRef = useRef<{ [key: string]: mapboxgl.Marker }>({});
	const tooltipRef = useRef<mapboxgl.Popup | null>(null);
	const popupRef = useRef<mapboxgl.Popup | null>(null);
	
	// Enhanced loading and processing states
	const [isLoading, setIsLoading] = useState(false);
	const [loadingProgress, setLoadingProgress] = useState(0);
	const [loadingMessage, setLoadingMessage] = useState('');
	const [showLocationWarning, setShowLocationWarning] = useState(false);
	const [shouldRenderLocations, setShouldRenderLocations] = useState(false);
	
	// Processing control refs
	const processingAbortController = useRef<AbortController | null>(null);
	const markerProcessingQueue = useRef<Location[]>([]);
	const isProcessingMarkers = useRef(false);
	const processingChunkSize = 50; // Process markers in chunks
	const processingDelay = 10; // ms delay between chunks
	
	const dispatch = useDispatch();
	const navigate = useNavigate();
	const {
		districtOptionState: { module },
	} = useSelector((state: RootState) => state.rootSlice.application);

	useEffect(() => {
		const { districtJsonVal, borderKeyVal } = geoJsonValueSelector(
			currentModule || module
		);

		setDistrictJson(districtJsonVal as GeoJSON.FeatureCollection);
		setBorderKey(borderKeyVal);
	}, [module, currentModule]);

	// Optimized marker cleanup and location handling
	useEffect(() => {
		// Abort any ongoing processing
		if (processingAbortController.current) {
			processingAbortController.current.abort();
		}

		// Clear existing markers efficiently
		const markers = Object.values(markersRef.current);
		if (markers.length > 0) {
			setIsLoading(true);
			setLoadingMessage('Clearing previous markers...');
			
			// Clear markers in chunks to prevent UI blocking
			const clearMarkersInChunks = async () => {
				const chunkSize = 100;
				for (let i = 0; i < markers.length; i += chunkSize) {
					const chunk = markers.slice(i, i + chunkSize);
					chunk.forEach(marker => marker.remove());
					
					// Update progress
					const progress = Math.min(((i + chunkSize) / markers.length) * 30, 30);
					setLoadingProgress(progress);
					
					// Yield to main thread
					await new Promise(resolve => setTimeout(resolve, 1));
				}
				markersRef.current = {};
			};

			clearMarkersInChunks().then(() => {
				// Handle new locations
				handleLocationChange();
			});
		} else {
			handleLocationChange();
		}
	}, [locations]);

	const handleLocationChange = () => {
		if (locations.length === 0) {
			setIsLoading(false);
			setLoadingProgress(0);
			setLoadingMessage('');
			setShouldRenderLocations(false);
			setShowLocationWarning(false);
			return;
		}

		if (locations.length > 1000) {
			// Show warning but start background processing
			setShowLocationWarning(true);
			setShouldRenderLocations(false);
			// Start background processing
			startBackgroundProcessing();
		} else {
			setShowLocationWarning(false);
			setShouldRenderLocations(true);
		}
	};

	// Background processing for large datasets
	const startBackgroundProcessing = async () => {
		if (locations.length === 0) return;

		setIsLoading(true);
		setLoadingMessage('Preparing map data...');
		setLoadingProgress(30);

		// Create new abort controller
		processingAbortController.current = new AbortController();
		const signal = processingAbortController.current.signal;

		try {
			// Process locations in background
			markerProcessingQueue.current = [...locations];
			await processMarkersInBackground(signal);
		} catch (error) {
			if (!signal.aborted) {
				console.error('Error processing markers:', error);
				setLoadingMessage('Error processing markers');
			}
		}
	};

	// Process markers in chunks to prevent UI blocking
	const processMarkersInBackground = async (signal: AbortSignal) => {
		if (!mapRef.current || isProcessingMarkers.current) return;

		isProcessingMarkers.current = true;
		const totalLocations = markerProcessingQueue.current.length;
		let processedCount = 0;

		try {
			while (markerProcessingQueue.current.length > 0 && !signal.aborted) {
				const chunk = markerProcessingQueue.current.splice(0, processingChunkSize);
				
				// Process chunk
				await processMarkerChunk(chunk, signal);
				
				processedCount += chunk.length;
				const progress = 30 + ((processedCount / totalLocations) * 70);
				setLoadingProgress(Math.min(progress, 100));
				setLoadingMessage(`Processing markers: ${processedCount}/${totalLocations}`);

				// Yield to main thread
				if (processingDelay > 0) {
					await new Promise(resolve => setTimeout(resolve, processingDelay));
				}
			}

			if (!signal.aborted) {
				setIsLoading(false);
				setLoadingProgress(100);
				setLoadingMessage('');
				// Auto-dismiss warning after background processing
				if (showLocationWarning) {
					setTimeout(() => {
						setShowLocationWarning(false);
						setShouldRenderLocations(true);
					}, 500);
				}
			}
		} finally {
			isProcessingMarkers.current = false;
		}
	};

	// Process a chunk of markers
	const processMarkerChunk = async (chunk: Location[], signal: AbortSignal) => {
		if (!mapRef.current || signal.aborted) return;

		return new Promise<void>((resolve) => {
			// Use requestAnimationFrame for smooth processing
			requestAnimationFrame(() => {
				if (signal.aborted) {
					resolve();
					return;
				}

				try {
					chunk.forEach((location) => {
						if (signal.aborted) return;
						createMarker(location);
					});
				} catch (error) {
					console.error('Error in marker chunk processing:', error);
				}
				resolve();
			});
		});
	};

	// Optimized marker creation
	const createMarker = (location: Location) => {
		if (!mapRef.current) return;

		const el = document.createElement('div');
		el.className = 'marker';
		el.style.width = '10px';
		el.style.height = '10px';
		el.style.borderRadius = '50%';
		el.style.backgroundColor = '#007bff';
		el.style.cursor = 'pointer';

		const marker = new mapboxgl.Marker(el)
			.setLngLat([location.longitude, location.latitude])
			.addTo(mapRef.current);

		markersRef.current[location.Address] = marker;

		// Optimized event listeners
		let isHovered = false;

		el.addEventListener('mouseenter', () => {
			if (isHovered) return;
			isHovered = true;
			
			tooltipRef.current
				?.setLngLat([location.longitude, location.latitude])
				.setHTML(`<span>${location.Address}</span>`)
				.addTo(mapRef.current!);
		});

		el.addEventListener('mouseleave', () => {
			isHovered = false;
			tooltipRef.current?.remove();
		});

		el.addEventListener('click', (e) => {
			e.stopPropagation();
			showPropertyPopup(location);
		});
	};

	// Optimized popup creation
	const showPropertyPopup = (location: Location) => {
		if (!mapRef.current) return;

		popupRef.current
			?.setLngLat([location.longitude, location.latitude])
			.setHTML(
				`
				<div class="p-4 bg-white rounded-lg shadow-lg text-center">
					<h3 class="text-base mb-2">${location.Address}</h3>
					<button
						class="px-4 py-2 bg-[#324B69] text-white rounded hover:bg-[#495f7c] transition-colors view-property-btn"
						data-address="${location.Address}"
						data-bbl="${location.BBL}"
						data-zipCode="${location['Zip Code']}"
					>
						View Property
					</button>
				</div>
				`
			)
			.addTo(mapRef.current);

		// Use more efficient event delegation
		setTimeout(() => {
			const btn = document.querySelector('.view-property-btn');
			if (btn) {
				btn.addEventListener('click', (event) => {
					const target = event.target as HTMLElement;
					dispatch(
						setAddressState({
							properties: {
								name: target.getAttribute('data-address') || '',
								borough: '',
								postalcode: target.getAttribute('data-zipCode') || '',
								label: '',
								addendum: {
									pad: {
										bbl: target.getAttribute('data-bbl') || '',
									},
								},
							},
						})
					);
					navigate('/property', { replace: true });
				});
			}
		}, 50);
	};

	// Initialize map only once
	useEffect(() => {
		if (!mapContainerRef.current || mapRef.current) return;

		const mapInstance = new mapboxgl.Map({
			container: mapContainerRef.current,
			style: process.env.REACT_APP_MAPBOX_STYLE_URL,
			center: [-73.935242, 40.73061],
			zoom: 10,
			minZoom: 9,
			maxZoom: 20,
			dragRotate: false,
			touchZoomRotate: false,
			pitchWithRotate: false,
			bearing: 0,
			pitch: 0,
		});

		mapRef.current = mapInstance;
		mapInstance.addControl(new mapboxgl.NavigationControl(), 'top-left');

		popupRef.current = new mapboxgl.Popup({
			closeButton: true,
			closeOnClick: false,
			className: 'custom-popup',
		});

		tooltipRef.current = new mapboxgl.Popup({
			closeButton: false,
			closeOnClick: false,
			className: 'custom-tooltip',
		});

		return () => {
			// Cleanup
			if (processingAbortController.current) {
				processingAbortController.current.abort();
			}
			mapRef.current?.remove();
			mapRef.current = null;
		};
	}, []);

	// Handle district data and label updates
	useEffect(() => {
		if (!mapRef.current || !districtJson) return;

		const map = mapRef.current;

		const initializeDistrictLayers = () => {
			map.addSource('districts', {
				type: 'geojson',
				data: districtJson,
			});

			map.addLayer({
				id: 'districts-border',
				type: 'line',
				source: 'districts',
				paint: {
					'line-color': '#ffffff',
					'line-width': 2,
				},
			});

			map.addLayer({
				id: 'districts-labels',
				type: 'symbol',
				source: 'districts',
				layout: {
					'text-field': ['get', borderKey],
					'text-size': 16,
					'text-anchor': 'center',
				},
				paint: {
					'text-color': '#ffffff',
					'text-halo-color': '#ffffff',
					'text-halo-width': 1.5,
				},
			});
		};

		const updateDistrictData = () => {
			(map.getSource('districts') as mapboxgl.GeoJSONSource).setData(districtJson);
			if (map.getLayer('districts-labels')) {
				map.setLayoutProperty('districts-labels', 'text-field', ['get', borderKey]);
			}
		};

		if (!map.loaded()) {
			map.once('load', () => {
				initializeDistrictLayers();
			});
		} else {
			if (!map.getSource('districts')) {
				initializeDistrictLayers();
			} else {
				updateDistrictData();
			}
		}
	}, [districtJson, borderKey]);

	// Handle visible locations rendering
	useEffect(() => {
		if (!mapRef.current || !shouldRenderLocations || !locations.length) return;

		setIsLoading(true);
		setLoadingMessage('Rendering markers...');
		setLoadingProgress(0);

		// Process visible locations
		const processVisibleLocations = async () => {
			processingAbortController.current = new AbortController();
			markerProcessingQueue.current = [...locations];
			await processMarkersInBackground(processingAbortController.current.signal);
		};

		processVisibleLocations();
	}, [shouldRenderLocations]);

	// Enhanced warning handlers
	const handleProceedAnyway = () => {
		setShowLocationWarning(false);
		setShouldRenderLocations(true);
	};

	const handleDismissWarning = () => {
		setShowLocationWarning(false);
		setShouldRenderLocations(false);
		// Stop background processing
		if (processingAbortController.current) {
			processingAbortController.current.abort();
		}
		setIsLoading(false);
		setLoadingProgress(0);
		setLoadingMessage('');
	};

	// Cleanup on unmount
	useEffect(() => {
		return () => {
			if (processingAbortController.current) {
				processingAbortController.current.abort();
			}
		};
	}, []);

	return (
		<div className={`relative w-full ${mapHeight}`}>
			<div ref={mapContainerRef} className="w-full h-full" />

			{showLocationWarning && (
				<div className="absolute inset-0 flex items-center justify-center bg-black bg-opacity-50 z-40">
					<div className="bg-white p-6 rounded-lg shadow-lg max-w-md z-50 m-2">
						<div className="flex justify-between items-start mb-4">
							<div className="text-lg font-semibold">Warning</div>
							<button
								onClick={handleDismissWarning}
								className="text-gray-500 hover:text-gray-700"
							>
								<IoMdClose className="w-5 h-5" />
							</button>
						</div>
						<p className="mb-4">
							More than 1000 properties are selected. Displaying all of them on
							the map may cause poor browser performance.
						</p>
						<p className="mb-4">
							Apply filters to reduce the number of properties selected to fewer
							than 1000 or dismiss this message to display the blank map.
						</p>
						<p className="mb-4 text-sm text-gray-600">
							If the page freezes, refresh your browser to reset.
						</p>
						<div className="flex justify-end gap-3">
							<button
								onClick={handleDismissWarning}
								className="px-4 py-2 bg-gray-200 text-gray-700 rounded hover:bg-gray-300"
							>
								Dismiss
							</button>
							<button
								onClick={handleProceedAnyway}
								className="px-4 py-2 bg-[#324B69] text-white rounded hover:bg-[#495f7c] transition-colors"
							>
								Proceed Anyway
							</button>
						</div>
					</div>
				</div>
			)}

			{/* Enhanced loading overlay */}
			{isLoading && shouldRenderLocations && (
				<div className="absolute inset-0 flex items-center justify-center bg-black bg-opacity-30 z-30">
					<div className="bg-white p-6 rounded-lg shadow-lg text-center">
						<CustomSpinner width={60} height={60} border={8} />
						<div className="mt-4">
							<p className="text-sm font-medium text-gray-700">{loadingMessage}</p>
							{loadingProgress > 0 && (
								<>
									<div className="mt-2 bg-gray-200 rounded-full h-2 w-48">
										<div 
											className="bg-blue-600 h-2 rounded-full transition-all duration-300"
											style={{ width: `${loadingProgress}%` }}
										/>
									</div>
									<p className="text-xs text-gray-500 mt-1">
										{Math.round(loadingProgress)}% complete
									</p>
								</>
							)}
						</div>
					</div>
				</div>
			)}

			{/* Background processing indicator (non-blocking) */}
			{isLoading && !shouldRenderLocations && !showLocationWarning && (
				<div className="absolute top-4 right-4 bg-white p-3 rounded-lg shadow-lg z-20 max-w-xs">
					<div className="flex items-center space-x-2">
						<div className="animate-spin rounded-full h-4 w-4 border-b-2 border-blue-600"></div>
						<div className="text-sm">
							<p className="font-medium text-gray-700">Processing in background</p>
							{loadingProgress > 0 && (
								<p className="text-xs text-gray-500">{Math.round(loadingProgress)}% complete</p>
							)}
						</div>
					</div>
					{loadingProgress > 0 && (
						<div className="mt-2 bg-gray-200 rounded-full h-1">
							<div 
								className="bg-blue-600 h-1 rounded-full transition-all duration-300"
								style={{ width: `${loadingProgress}%` }}
							/>
						</div>
					)}
				</div>
			)}

			<style>{`
				.custom-tooltip {
					background: white;
					border-radius: 4px;
					color: #333;
					padding: 2px 4px;
					font-size: 12px;
					pointer-events: none;
				}
				.custom-tooltip .mapboxgl-popup-content {
					padding: 4px;
					border-radius: 4px;
				}
				.custom-popup .mapboxgl-popup-content {
					padding: 0;
					border-radius: 8px;
				}
				.mapboxgl-popup-close-button {
					right: 6px !important;
					top: 4px !important;
					font-size: larger !important;
				}
				.mapboxgl-popup-close-button:hover {
					background-color: transparent !important;
					color: #000 !important;
				}
			`}</style>
		</div>
	);
};